import { useMemo } from 'react'
import type { UIMessage } from 'ai'
import { isToolUIPart } from 'ai'
import { useTranslation } from 'react-i18next'
import { Shimmer } from '@/components/ai-elements/shimmer'

export type LlmChatAssistantPendingStatusProps = {
  parts: UIMessage['parts']
}

/**
 * Shown when the assistant row is active but no streaming text exists yet
 * (e.g. between reasoning-end and the first tool, between tool calls, or
 * after the last tool finishes and before text-start). Replaces the bare
 * blinking caret with a context-aware shimmer message so the user always
 * sees what the assistant is doing.
 */
export function LlmChatAssistantPendingStatus({
  parts,
}: LlmChatAssistantPendingStatusProps) {
  const { t } = useTranslation('llmChat')

  const statusKey = useMemo(() => {
    let lastNonStepPart: UIMessage['parts'][number] | null = null
    for (let idx = parts.length - 1; idx >= 0; idx -= 1) {
      const candidate = parts[idx]
      if (candidate.type !== 'step-start') {
        lastNonStepPart = candidate
        break
      }
    }

    if (lastNonStepPart === null) {
      return 'assistantStatusWorking'
    }
    if (
      isToolUIPart(lastNonStepPart) &&
      (lastNonStepPart.state === 'output-available' ||
        lastNonStepPart.state === 'output-error')
    ) {
      return 'assistantStatusComposing'
    }
    if (lastNonStepPart.type === 'reasoning') {
      return 'assistantStatusThinking'
    }
    return 'assistantStatusWorking'
  }, [parts])

  return (
    <Shimmer
      duration={1.5}
      className='text-[13px] leading-6 text-muted-foreground'
    >
      {t(statusKey)}
    </Shimmer>
  )
}
